/**
 * Reading a spreadsheet somebody exported.
 *
 * Vinnie settled the format on 19 August 2026: CSV, because every spreadsheet
 * on earth exports it and it needs no new package. That decision is only cheap
 * if the reading is done properly, so this is a real parser rather than a
 * split on commas.
 *
 * WHAT A SPLIT ON COMMAS GETS WRONG, using TRINRE's own file as the witness.
 * Row LH042 reads:
 *
 *     ...,"When the customer does not pay, CPFC has the option to cancel it.
 *     On cancellation inform TRINE ... (Time on Risk Stuff)",8,Blocked,...
 *
 * One cell, holding a comma AND a line break. Split on commas and that row
 * becomes two broken rows, the columns after it shift left by one, and every
 * value from Difficulty onwards is read out of the wrong column — quietly, with
 * no error, for that row and no other. So: quotes, doubled quotes inside
 * quotes, and line breaks inside quotes all handled here.
 *
 * THE DELIMITER IS NOT ALWAYS A COMMA. Excel writes the list separator of the
 * machine it is running on. In much of Europe and Latin America that is a
 * semicolon, and a file saved there is not readable by anything that assumes a
 * comma. FlightDeck is sold beyond one office, so the separator is sniffed
 * rather than assumed — see pickDelimiter.
 */

export type Delimiter = ',' | ';' | '\t' | '|';

export type Sheet = {
  /** The first row. Blank headings become "Column 3" so every column can be named. */
  headers: string[];
  /** Everything after the first row, each padded to the width of the headers. */
  rows: string[][];
  /** What separated the cells. Shown to the person, so they can see it was read right. */
  delimiter: Delimiter;
  /**
   * Rows whose cell count did not match the headers, by their line number in the
   * file. Padded rather than dropped — "we never drop them" — but counted, so
   * the screen can say so instead of pretending the file was clean.
   */
  ragged: number[];
  /**
   * Which kind of file these rows came from. Absent means CSV — the original
   * path, and the only one that has a delimiter to speak of. An Excel workbook
   * sets 'xlsx' and carries the sheet it was read from (#368), so the screen can
   * say "sheet Backlog" where it would otherwise say "separated by tab".
   */
  source?: 'csv' | 'xlsx';
  /** The workbook sheet these rows are from, and every sheet it offered. xlsx only. */
  sheetName?: string;
  sheetNames?: string[];
};

const CANDIDATES: Delimiter[] = [',', ';', '\t', '|'];

/**
 * Which character separates the cells.
 *
 * Counts each candidate OUTSIDE quotes on the first few lines and takes the one
 * that both appears and gives every line the same number of cells. Consistency
 * is the test, not frequency: prose in a description column can easily hold
 * more commas than a semicolon-separated file has semicolons, so counting alone
 * picks the wrong one on exactly the files this exists for.
 */
export function pickDelimiter(text: string): Delimiter {
  let best: Delimiter = ',';
  let bestScore = -1;
  for (const d of CANDIDATES) {
    const rows = readRows(text, d).slice(0, 20);
    if (rows.length === 0) continue;
    const width = rows[0].length;
    if (width < 2) continue;
    const steady = rows.every((r) => r.length === width);
    // A file that parses into the same width every time, with the most columns,
    // is the right reading of it.
    const score = (steady ? 1000 : 0) + width;
    if (score > bestScore) { bestScore = score; best = d; }
  }
  return best;
}

/**
 * The parse itself — one pass, character by character.
 *
 * Written as a loop rather than a regular expression because the state that
 * matters (are we inside quotes) is exactly what a regular expression cannot
 * hold, and every "CSV regex" on the internet is wrong about the file above.
 */
function readRows(text: string, d: Delimiter): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let started = false;   // has this row got any content at all

  const endCell = () => { row.push(cell); cell = ''; started = true; };
  const endRow = () => { endCell(); rows.push(row); row = []; started = false; };

  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (quoted) {
      if (c !== '"') { cell += c; continue; }
      // A doubled quote inside a quoted cell is one literal quote.
      if (text[i + 1] === '"') { cell += '"'; i += 1; continue; }
      quoted = false;
      continue;
    }
    if (c === '"' && cell === '') { quoted = true; started = true; continue; }
    if (c === d) { endCell(); continue; }
    if (c === '\r') {
      if (text[i + 1] === '\n') i += 1;   // CRLF is one ending, not two
      endRow();
      continue;
    }
    if (c === '\n') { endRow(); continue; }
    cell += c;
    started = true;
  }
  // A file that does not end in a newline still has a last row.
  if (started || cell !== '' || row.length) endRow();
  // Excel likes to leave a trailing empty line. One empty cell is not a row.
  while (rows.length && rows[rows.length - 1].every((v) => v === '')) rows.pop();
  return rows;
}

/**
 * Turn the text of a file into headers and rows.
 *
 * @param text     the whole file
 * @param force    a separator to use instead of sniffing, when the person
 *                 disagrees with what was sniffed
 */
export function parseCsv(text: string, force?: Delimiter): Sheet {
  // A byte order mark is invisible and turns the first heading into "﻿Item
  // Id", which then matches nothing. Excel writes one on every UTF-8 export.
  const clean = text.replace(/^﻿/, '');
  const delimiter = force ?? pickDelimiter(clean);
  const all = readRows(clean, delimiter);
  if (all.length === 0) return { headers: [], rows: [], delimiter, ragged: [] };

  const headers = all[0].map((h, i) => h.trim() || `Column ${i + 1}`);
  const ragged: number[] = [];
  const rows = all.slice(1).map((r, i) => {
    if (r.length !== headers.length) ragged.push(i + 2);   // +2: 1-based, past the header
    const out = r.slice(0, headers.length);
    while (out.length < headers.length) out.push('');
    return out;
  });
  return { headers, rows, delimiter, ragged };
}

/**
 * Is this a file of text at all, or something that only has a .csv on the end?
 *
 * Vinnie at step 6.7 of the walk, 20 August 2026: the file picker only offers
 * CSVs, so he could not select anything else to find out. Prevention is better
 * than an error — but a picker is not a guarantee. Every file dialog has an
 * "All Files" escape, and renaming a spreadsheet to .csv is a thing people
 * genuinely do when software asks them for a CSV.
 *
 * What happens without this is not dangerous — nothing imports, because no
 * column can be the title — but it is bewildering: an .xlsx renamed shows a
 * mapping screen listing 1,906 rows and one column headed "PK♦♦♦", with a red
 * line underneath complaining that no column has been set as the title. The
 * true problem is not the title.
 *
 * A NUL byte settles it — text files do not have them, and every compressed or
 * image format does. Failing that, a wedge of characters no keyboard makes.
 * Deliberately NOT "it only found one column": a single column of task names is
 * a thin spreadsheet, but it is a real one and it should import.
 */
export function looksBinary(text: string): boolean {
  const sample = text.slice(0, 8000);
  if (!sample) return false;
  if (sample.includes('\u0000')) return true;
  // Control characters that are not tab, newline or carriage return.
  let odd = 0;
  for (let i = 0; i < sample.length; i += 1) {
    const c = sample.charCodeAt(i);
    if (c === 9 || c === 10 || c === 13) continue;
    if (c < 32 || c === 127) odd += 1;
  }
  return odd / sample.length > 0.01;
}

/** Every distinct value in a column, most common first, with how many rows have it. */
export function valuesIn(sheet: Sheet, column: number): { value: string; rows: number }[] {
  const seen = new Map<string, number>();
  for (const r of sheet.rows) {
    const v = (r[column] ?? '').trim();
    if (!v) continue;
    seen.set(v, (seen.get(v) ?? 0) + 1);
  }
  return [...seen].map(([value, rows]) => ({ value, rows })).sort((a, b) => b.rows - a.rows || a.value.localeCompare(b.value));
}
